import type { FunctionComponent } from "react";
import { useContext, useEffect } from "react";
import { isMobile, withOrientationChange } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Components/Header";
import Home from "./Components/Home";
import { useSentryUserSetter } from "./utilities/customHooks";
import { getConfigs } from "./utilities/helperFuncs";
import { SocketContext } from "./utilities/socketService";
import { getDeviceInfo } from "@wolzienllc/vcc-react-commons";
import {
  setDeviceInfo,
  setError,
  setInitialized,
  setPageConfig,
  setParticipantData,
} from "./state/actions";
import { RootState } from "./state";
import * as Sentry from "@sentry/react";
import "./App.css";
import { PageConfiguration } from "./state/types";
import { getPageErrorType } from "./utilities/pageErrors";

import {
  ThemeProvider,
  Theme,
  StyledEngineProvider,
  createTheme,
} from "@mui/material/styles";
import { amber, blueGrey, lightGreen, red } from "@mui/material/colors";
import Connecting from "./Components/ContentAreaText/Connecting";
import ErrorAlert from "./Components/ContentAreaText/ErrorAlert";
import { Alert } from "@mui/material";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const theme = createTheme({
  typography: {
    fontFamily: "'Open Sans', sans-serif",
    fontSize: 15,
  },
  palette: {
    info: blueGrey,
    success: lightGreen,
    mode: "light",
    secondary: red,
    warning: amber,
  },
});

type Props = {
  isLandscape?: boolean;
};

const App: FunctionComponent<Props> = (props) => {
  const { isLandscape } = props;
  const socketService = useContext(SocketContext);
  const error = useSelector((state: RootState) => state.error);

  const initialized = useSelector((state: RootState) => state.initialized);

  const socketReconnecting = useSelector(
    (state: RootState) => state.socketReconnecting
  );

  const shouldDisplayError = initialized && Boolean(error);

  const participantData = useSelector(
    (state: RootState) => state.participantData
  );

  useSentryUserSetter(participantData);

  const dispatch = useDispatch();

  const incorrectParamsMessage = "Incorrect parameters provided.";

  // handles loading the template
  useEffect(() => {
    if (!initialized) {
      (async () => {
        try {
          const params = new URLSearchParams(
            document.location.search.substring(1)
          );

          const sessionGuid = params.get("session");

          if (!sessionGuid) {
            throw new Error(incorrectParamsMessage);
          }

          const { page: pageConfig, participant: participantConfig } =
            await getConfigs(sessionGuid);

          dispatch(setPageConfig(pageConfig));
          dispatch(setParticipantData(participantConfig));

          const deviceInfo = getDeviceInfo();
          dispatch(setDeviceInfo(deviceInfo));
          dispatch(setError(undefined));
          dispatch(setInitialized());

          socketService.init(sessionGuid, deviceInfo);
        } catch (err) {
          //console.warn("Error during initialization:");
          //console.dir(err);
          let res:
            | { pageConfig?: PageConfiguration; reason?: string }
            | undefined;

          try {
            res = await err?.response?.json?.();

            if (res?.pageConfig) {
              //console.log("Setting page config from error response");
              dispatch(setPageConfig(res.pageConfig));
            }
          } catch (configErr) {
            console.warn("No page config in error response");
            //console.dir(configErr);
          }

          const status = err?.response?.status;
          //console.log("Error status:", status);

          // Prefer the server's reason. A cancelled invitation and a session in
          // use both return 403, so the status alone reported "in use" for an
          // invitation that a producer had cancelled.
          const errorType = getPageErrorType(res?.reason, status);

          if (errorType === undefined) {
            dispatch(setError({ message: err?.message }));
          } else {
            dispatch(setError({ type: errorType }));
          }
          dispatch(setInitialized());
        }
      })();
    }
  }, [dispatch, initialized, socketService]);

  // Close the socket when the page goes away. This cleanup used to be returned
  // from the async function inside the effect above, so React never received it
  // and the socket, its listeners, and its retry timer were never released. It
  // cannot live in that effect either: `initialized` is one of its dependencies,
  // so the cleanup would fire as soon as initialization succeeded and close the
  // socket that had just been opened.
  useEffect(() => {
    return () => socketService.disconnect();
  }, [socketService]);

  let wakeLock = null;

  const requestWakeLock = async () => {
    try {
      // @ts-ignore
      wakeLock = await navigator?.wakeLock?.request("screen");

      wakeLock?.addEventListener("release", () => {
        console.log("Screen Wake Lock released:", wakeLock?.released);
      });
      //console.log("Screen Wake Lock engaged:", wakeLock.released);
    } catch (err) {
      console.error(`Screen Wake Lock prevention error: ${err.message}`);
    }
  };

  useEffect(() => {
    requestWakeLock();

    return () => {
      if (Boolean(wakeLock)) {
        wakeLock.release();
        wakeLock = null;
      }
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <div data-testid="App" id="app">
          {initialized && !shouldDisplayError ? (
            <>
              {/* The server refuses a new handshake while the previous socket
                  is still counted as connected. Tell the guest that the page is
                  retrying, so a silent page does not make them reload. */}
              {socketReconnecting && (
                <Alert
                  className="fullWidth"
                  data-testid="socket-reconnecting"
                  icon={false}
                  severity="info"
                  sx={{ display: "block", textAlign: "center" }}
                >
                  Reconnecting ...
                </Alert>
              )}
              <Header isLandscape={isLandscape} />
              <Home isLandscape={isLandscape} />
            </>
          ) : (
            <>
              <div
                id={
                  isMobile
                    ? "main-content-wrapper-mobile"
                    : "main-content-wrapper"
                }
                style={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                {shouldDisplayError ? (
                  <>
                    {/*<div>
                      Error placeholder:
                      <br /> {JSON.stringify(error)}
                    </div>
                    <br />*/}
                    <ErrorAlert error={error} />
                  </>
                ) : (
                  <Connecting />
                )}
              </div>
            </>
          )}
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default Sentry.withProfiler(withOrientationChange(App));
